/**
 * Calendar reminder scheduler — a 30 s interval scan over upcoming events
 * (useAppUpdates.js's setInterval-in-effect precedent, hoisted into a plain
 * service started once from App.js's boot effect and testable with fake
 * timers).
 *
 * Honest contract (PLAN §3.6 / F): reminders fire only while the Apiboo
 * process runs — including minimized-to-tray, but not after Quit. The event
 * editor states this in its copy. Free/local users get reminders with zero
 * cloud involvement.
 *
 * Firing = in-app toast ALWAYS (notificationStore) + OS toast only when the
 * window is unfocused AND the desktopNotifications preference is on (the
 * chatAlerts.js gating shape). Each (event, reminder) fires ONCE: dedupe keys
 * `<uid>:<minutes_before>:<occurrenceStart>` persist in account-namespaced
 * localStorage (apibooKey) so a restart doesn't re-fire, and the in-memory
 * mirror clears on session end (channelService invariant).
 *
 * Reminders whose event is already more than LATE_FIRE_WINDOW_MS past are
 * swallowed. The FIRST scan after launch counts those within MISSED_WINDOW_MS
 * and surfaces ONE aggregated in-app notification, so "Apiboo was closed" is
 * something the user is told rather than something they infer from silence.
 */
import i18n from '../i18n';
import { listEvents } from '../extensions/bundled/calendar/calendarStore';
import { eventRange, formatEventTime, addDays } from '../utils/calendarDates';
import { expandEvents } from '../utils/calendarRecurrence';
import { showCalendarReminderNotification } from '../utils/calendarNotification';
import { useNotificationStore } from '../store/notificationStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { apibooKey } from './storageNamespace';
import { addSessionEndedListener } from './channelService';

export const SCAN_INTERVAL_MS = 30_000;
/** Staleness is judged against the EVENT START, not the reminder's fire-at
 *  (fix round 1, B2): a missed fire-time must still ring while the event is
 *  ahead (event tomorrow + "1 day before" picked tonight), and an "at start"
 *  reminder needs a grace window past the start or the 30 s scan cadence
 *  would always lose the race. Only an event already MORE than this far into
 *  its past is stale — marked fired, never shown (firing Friday's toasts on
 *  Monday helps nobody). */
export const LATE_FIRE_WINDOW_MS = 30 * 60_000;
/** How far back the FIRST scan after launch looks for reminders that were
 *  swallowed while Apiboo was closed (PLAN §3.5, clause 4). A hard inner
 *  bound, deliberately independent of `scanAndFireReminders`'s now−1d
 *  expansion window: someone returning after a week sees nothing. */
export const MISSED_WINDOW_MS = 12 * 3_600_000;
const FIRED_KEY_SUFFIX = 'calendar-fired-reminders';
const FIRED_RETENTION_MS = 48 * 3_600_000;

let intervalId = null;
/** Missed reminders are surfaced ONCE, on the first scan of this process
 *  (clause 1). Every later scan reports nothing regardless of state. */
let firstScanDone = false;
// In-memory mirror of the persisted dedupe set for the CURRENT account.
// Session-derived (keys belong to whoever is signed in), so it clears on
// session end — the localStorage copy is per-account-namespaced already.
let firedInMemory = new Set();

addSessionEndedListener(() => {
  firedInMemory = new Set();
});

function storageKey() {
  return apibooKey(FIRED_KEY_SUFFIX);
}

function readFiredMap() {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeFiredMap(map) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(map));
  } catch {
    /* quota / disabled storage — in-memory set still dedupes this session */
  }
}

export function reminderDedupeKey(event, reminder) {
  const occurrenceStart = event.all_day ? event.start_date : event.start;
  return `${event.uid}:${reminder.minutes_before}:${occurrenceStart}`;
}

function hasFired(key, firedMap) {
  return firedInMemory.has(key) || firedMap[key] != null;
}

function markFired(key, firedMap, nowMs) {
  firedInMemory.add(key);
  firedMap[key] = nowMs;
  // Prune old entries so the map cannot grow without bound.
  for (const k of Object.keys(firedMap)) {
    if (nowMs - firedMap[k] > FIRED_RETENTION_MS) delete firedMap[k];
  }
  writeFiredMap(firedMap);
}

/**
 * A moved event gets a new uid (calendarMove.js), and the dedupe key starts
 * with the uid — without this a reminder that already fired rings once more
 * (card 25e3e5a2). Copies every fired key of `oldUid` to `newUid`.
 */
export function carryFiredReminders(oldUid, newUid) {
  if (!oldUid || !newUid || oldUid === newUid) return;
  const prefix = `${oldUid}:`;
  const firedMap = readFiredMap();
  let changed = false;
  for (const key of new Set([...firedInMemory, ...Object.keys(firedMap)])) {
    if (!key.startsWith(prefix)) continue;
    const moved = `${newUid}:${key.slice(prefix.length)}`;
    firedInMemory.add(moved);
    if (firedMap[key] != null) {
      firedMap[moved] = firedMap[key];
      changed = true;
    }
  }
  if (changed) writeFiredMap(firedMap);
}

function fireReminder(event, timeFormat) {
  const title = event.title || i18n.t('calendar.untitledEvent');
  const body = formatEventTime(event, timeFormat) + (event.location ? ` · ${event.location}` : '');
  const notificationTitle = i18n.t('calendar.reminder.notificationTitle', { title });
  // In-app toast: always.
  useNotificationStore.getState().addNotification({
    type: 'info',
    title: notificationTitle,
    message: body,
    duration: 10_000,
  });
  // OS toast: only when the window is unfocused and the pref (opt-OUT —
  // undefined means on, chatAlerts precedent) allows it. NOTE: this is the
  // SAME `desktopNotifications` pref the chat path reads
  // (utils/chatAlerts.js) — one switch, two features. Its description in
  // settings must name both; reminderHonestyCopy.test.js pins that.
  const prefs = usePreferencesStore.getState();
  const hasFocus = typeof document !== 'undefined' && document.hasFocus && document.hasFocus();
  if (!hasFocus && prefs.desktopNotifications !== false) {
    showCalendarReminderNotification({ title: notificationTitle, body });
  }
}

/**
 * One aggregated IN-APP notification for reminders that were swallowed as
 * stale while Apiboo was closed (PLAN §3.5). Never an OS toast — it must not
 * fire into a tray nobody is looking at, and it deliberately does not consult
 * the `desktopNotifications` gate. Count 0 → nothing at all.
 */
function reportMissedReminders(count) {
  if (count <= 0) return;
  useNotificationStore.getState().addNotification({
    type: 'info',
    title: i18n.t('calendar.toast.missedRemindersTitle'),
    message: i18n.t('calendar.toast.missedRemindersN', { count }),
    duration: 10_000,
  });
}

/**
 * One scan pass: fire every (event, reminder) whose fire-at instant is due
 * and not yet fired. Exported for tests (fake timers drive the interval; this
 * is also directly drivable with an injected `now`).
 */
export async function scanAndFireReminders(now = new Date()) {
  let events;
  try {
    // Phase 4: scan EXPANDED instances — now−1d..now+8d covers the largest
    // offered minutes_before (1 day) with slack; each instance carries its
    // own start, so the per-occurrence dedupe key needs no change, and
    // exdated instances never expand → never remind.
    events = expandEvents(await listEvents(), addDays(now, -1), addDays(now, 8));
  } catch {
    return; // records backend unavailable (browser dev) — nothing to scan
  }
  const nowMs = now.getTime();
  const timeFormat = usePreferencesStore.getState().timeFormat;
  const firedMap = readFiredMap();
  // Only the FIRST scan of this process counts missed reminders (clause 1).
  const isFirstScan = !firstScanDone;
  firstScanDone = true;
  let missedCount = 0;
  for (const event of events) {
    if (event.status === 'cancelled') continue;
    if (!Array.isArray(event.reminders) || event.reminders.length === 0) continue;
    const { start } = eventRange(event);
    const startMs = start.getTime();
    for (const reminder of event.reminders) {
      const fireAtMs = startMs - Number(reminder.minutes_before) * 60_000;
      // Corrupted record (garbage `start` → NaN): never toast "Invalid Date",
      // never write a dedupe key for it (fix round 1, N3).
      if (!Number.isFinite(fireAtMs)) continue;
      if (fireAtMs > nowMs) continue; // not due yet
      const key = reminderDedupeKey(event, reminder);
      if (hasFired(key, firedMap)) continue;
      // Mark BEFORE deciding staleness: a stale reminder must never fire
      // later either.
      markFired(key, firedMap, nowMs);
      // B2 policy: fire as long as the event hasn't started (however late the
      // fire-time is), plus a short grace past the start; swallow only truly
      // bygone events.
      if (nowMs - startMs > LATE_FIRE_WINDOW_MS) {
        // Event long past — stale, swallowed exactly as before (clause 6:
        // still marked fired above, never shown). On the first scan after
        // launch it is COUNTED, so the user learns something was missed
        // instead of nothing happening at all.
        if (isFirstScan && nowMs - startMs <= MISSED_WINDOW_MS) missedCount += 1;
        continue;
      }
      fireReminder(event, timeFormat);
    }
  }
  if (isFirstScan) reportMissedReminders(missedCount);
}

/**
 * Start the 30 s scanner (idempotent). BOOT-OWNED (fix round 1, B1): the one
 * production caller is App.js's mount effect, so reminders fire while Apiboo
 * runs regardless of which tab is active — CalendarView deliberately does NOT
 * start or stop it (the module-level singleton means any unmount cleanup here
 * would kill the global interval). Returns the stop function for App-teardown
 * cleanup and tests. An immediate first scan runs so a due reminder doesn't
 * wait half a minute after launch.
 */
export function startCalendarReminderScheduler() {
  if (intervalId != null) return stopCalendarReminderScheduler;
  scanAndFireReminders();
  intervalId = setInterval(() => scanAndFireReminders(), SCAN_INTERVAL_MS);
  return stopCalendarReminderScheduler;
}

export function stopCalendarReminderScheduler() {
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

/** Test hook: reset module state (interval + in-memory dedupe). */
export function _resetSchedulerForTests() {
  stopCalendarReminderScheduler();
  firedInMemory = new Set();
  firstScanDone = false;
}
